import {
  changePasswordApiV1AuthChangePasswordPost,
  forgotPasswordApiV1AuthForgotPasswordPost,
  resetPasswordApiV1AuthResetPasswordPost,
} from '@closet-theory/api';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@ui/components/base/buttons/button';
import { Callout } from '@ui/console/primitives';
import { FormCard, TextField } from '@ui/console/forms';
import { configureAdminApi, handleUnauthorized, signOut } from './auth';
import { Actions, Page } from './components';
import { PasswordField } from './PasswordField';

function textField(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === 'string' ? value : '';
}

export function ForgotPassword() {
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = textField(new FormData(event.currentTarget), 'email');
    const result = await forgotPasswordApiV1AuthForgotPasswordPost({ body: { email } });
    if (result.error) {
      setError('The reset request could not be completed.');
      return;
    }
    setError(undefined);
    setMessage('Check your email for the reset link.');
  };
  return (
    <div className="auth-page">
      <Page
        title="Reset access"
        description="Enter your work email and we will send you a link for setting a new password. The link stops working after a short while, so use it soon after it arrives."
      >
        <FormCard title="Reset link" onSubmit={(event) => void submit(event)}>
          <TextField label="Email" name="email" type="email" autoComplete="username" required />
          {error ? <Callout tone="error" title={error} /> : null}
          {message ? <Callout tone="success" title={message} /> : null}
          <Actions>
            <Button size="md" color="link-gray" href="/sign-in">
              Back to sign in
            </Button>
            <Button size="md" color="primary" type="submit">
              Send reset link
            </Button>
          </Actions>
        </FormCard>
      </Page>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const token = params.get('token') ?? '';
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const password = textField(new FormData(event.currentTarget), 'password');
    const result = await resetPasswordApiV1AuthResetPasswordPost({ body: { token, password } });
    if (result.error) {
      setError('This reset link is invalid or expired.');
      return;
    }
    void navigate('/sign-in', { replace: true });
  };
  return (
    <div className="auth-page">
      <Page
        title="Choose a new password"
        description="Set the password you will use from now on. This page only works from a link we emailed you, and only once."
      >
        <FormCard title="New password" onSubmit={(event) => void submit(event)}>
          {!token ? <Callout tone="error" title="This reset link is missing its token." /> : null}
          <PasswordField
            label="New password"
            name="password"
            autoComplete="new-password"
            minLength={12}
            required
          />
          {error ? <Callout tone="error" title={error} /> : null}
          <Actions>
            <Button size="md" color="primary" type="submit" isDisabled={!token}>
              Update password
            </Button>
          </Actions>
        </FormCard>
      </Page>
    </div>
  );
}

export function ChangePassword() {
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const newPassword = textField(data, 'newPassword');
    const confirmation = textField(data, 'confirmation');
    const currentPassword = textField(data, 'currentPassword');
    if (newPassword !== confirmation) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('Choose a password different from the current password.');
      return;
    }
    configureAdminApi();
    const result = await changePasswordApiV1AuthChangePasswordPost({
      body: {
        current_password: currentPassword,
        new_password: newPassword,
      },
    });
    handleUnauthorized(result.response.status);
    if (result.error) {
      setError('The password could not be changed.');
      return;
    }
    signOut();
    void navigate('/sign-in', { replace: true });
  };
  return (
    <Page
      title="Change password"
      description="Replace the password on your own staff account. You stay signed in here, but anywhere else you are signed in will need the new password."
    >
      <FormCard title="Password" onSubmit={(event) => void submit(event)}>
        <PasswordField
          label="Current password"
          name="currentPassword"
          autoComplete="current-password"
          required
        />
        <PasswordField
          label="New password"
          name="newPassword"
          autoComplete="new-password"
          minLength={12}
          required
        />
        <PasswordField
          label="Confirm new password"
          name="confirmation"
          autoComplete="new-password"
          minLength={12}
          required
        />
        {error ? <Callout tone="error" title={error} /> : null}
        <Actions>
          <Button size="md" color="primary" type="submit">
            Change password
          </Button>
        </Actions>
      </FormCard>
    </Page>
  );
}
